import { useMemo, useState } from 'react';
import { Link, generatePath } from 'react-router-dom';
import { Circle, CircleAlert } from 'lucide-react';

import { Alert, AlertTypes } from '@/models/AccountMetrics';
import { AppRoutes } from '@/utils/routes/router';

import ChurnSignalModal from './ChurnSignalModal';
import { CustomCard } from './MetricComponents';

const AlertItem = ({ alert, onSignalClick }: { alert: Alert; onSignalClick: (signal: any) => void }) => {
  const timeSince = (date: Date) => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + ' years ago';
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + ' months ago';
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + ' days ago';
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + ' hours ago';
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + ' minutes ago';
    return Math.floor(seconds) + ' seconds ago';
  };

  const alertTypeIcon = (alertType: AlertTypes) => {
    switch (alertType) {
      case 'critical':
        return <CircleAlert className="text-custom-red" />;
      case 'warning':
        return <Circle className="text-custom-yellow" />;
      default:
        return '';
    }
  };

  return (
    <div
      className="rounded-lg border border-custom-lightSilver px-4 py-3 mb-2 bg-white cursor-pointer hover:border-custom-darkSilver hover:shadow-[0_2px_4px_0_#f0f0f2]"
      onClick={() => onSignalClick(alert)}
    >
      <div className="flex justify-between items-center">
        <div className="mr-3">{alertTypeIcon(alert.type)}</div>

        <div className="flex-1">
          <h5 className="font-bold text-sm">{alert.title}</h5>
          <p className="text-xs text-custom-darkSilver mt-1 mb-1">{alert.description}</p>
          <div className="flex items-center">
            {/* Show user avatar or initials */}
            <span className="w-4 h-4 rounded-full mr-1 bg-custom-darkSilver flex items-center justify-center text-custom-lightSilver font-normal text-[8px] self-start">
              {alert.userName?.charAt(0).toUpperCase() || 'U'}
            </span>
            {alert.userId ? (
              <Link
                to={generatePath(AppRoutes.memberProfile, { id: alert.userId })}
                onClick={(e) => e.stopPropagation()}
                className="text-xs text-custom-blue hover:text-custom-darkBlue hover:underline font-medium"
              >
                {alert.userName || 'Unknown User'}
              </Link>
            ) : (
              <span className="text-xs text-custom-darkSilver">{alert.userName || 'Unknown User'}</span>
            )}
          </div>
        </div>

        <div className={`bg-custom-blue/10 border-custom-blue/50 text-custom-blue text-xs px-2 py-1 rounded-lg`}>
          {timeSince(new Date(alert.timestamp))}
        </div>
      </div>
    </div>
  );
};

interface AlertPanelProps {
  alerts: Alert[];
}

function alertTimeMs(a: Alert): number {
  const t = new Date(a.timestamp).getTime();
  return Number.isFinite(t) ? t : 0;
}

export const AlertPanel = ({ alerts }: AlertPanelProps) => {
  const [selectedSignal, setSelectedSignal] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const alertsNewestFirst = useMemo(
    () =>
      [...(alerts ?? [])].sort((a, b) => {
        const tb = alertTimeMs(b);
        const ta = alertTimeMs(a);
        return tb - ta;
      }),
    [alerts],
  );

  const handleSignalClick = (signal: any) => {
    // Use originalSignal if available, otherwise use the signal itself
    setSelectedSignal(signal.originalSignal || signal);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSignal(null);
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 mb-3">
        <h3 className="text-xl font-semibold text-custom-black">Alerts & Actions</h3>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 ml-auto">
          <Link
            to={`${AppRoutes.signals}?view=not-relevant`}
            className="text-custom-blue text-sm font-semibold transition-colors border-b-2 border-white hover:border-custom-blue"
          >
            Not relevant
          </Link>
          <Link
            to={AppRoutes.signals}
            className="text-custom-blue text-sm font-semibold transition-colors border-b-2 border-white hover:border-custom-blue"
          >
            View all signals →
          </Link>
        </div>
      </div>

      {alerts.length === 0 ? (
        <CustomCard>
          <p className="text-custom-darkSilver text-center py-8">No alerts at this time</p>
        </CustomCard>
      ) : (
        <div>
          {alertsNewestFirst.map((alert) => (
            <AlertItem key={alert.id} alert={alert} onSignalClick={handleSignalClick} />
          ))}
        </div>
      )}

      <ChurnSignalModal isOpen={isModalOpen} onClose={handleCloseModal} signal={selectedSignal} />
    </div>
  );
};
